import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import { hrmsGetRequest, hrmsPostRequest, hrmsUpdateRequest } from "../services/index";

const API_URL = "/api/performance-reviews";

// Create performance review (Admin) - POST /
export const createPerformanceReview = createAsyncThunk(
  "performanceReview/create",
  async (reviewData, { rejectWithValue }) => {
    try {
      const response = await hrmsPostRequest(API_URL, reviewData);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || error.message);
    }
  }
);

// Get performance reviews by teacher email - GET /teacher/:email
export const fetchPerformanceReviewsByTeacher = createAsyncThunk(
  "performanceReview/fetchByTeacher",
  async (teacherEmail, { rejectWithValue }) => {
    try {
      const response = await hrmsGetRequest(`${API_URL}/teacher/${teacherEmail}`);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || error.message);
    }
  }
);

// Get performance review by ID - GET /:id
export const fetchPerformanceReviewById = createAsyncThunk(
  "performanceReview/fetchById",
  async (reviewId, { rejectWithValue }) => {
    try {
      const response = await hrmsGetRequest(`${API_URL}/${reviewId}`);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || error.message);
    }
  }
);

// Update admin comments - PUT /:id/admin-comments
export const updateAdminComments = createAsyncThunk(
  "performanceReview/updateAdminComments",
  async ({ reviewId, adminComments }, { rejectWithValue }) => {
    try {
      const response = await hrmsUpdateRequest(`${API_URL}/${reviewId}/admin-comments`, {
        adminComments,
      });
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || error.message);
    }
  }
);

// Update employee comments - PUT /:id/employee-comments
export const updateEmployeeComments = createAsyncThunk(
  "performanceReview/updateEmployeeComments",
  async ({ reviewId, employeeComments }, { rejectWithValue }) => {
    try {
      const response = await hrmsUpdateRequest(`${API_URL}/${reviewId}/employee-comments`, {
        employeeComments,
      });
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || error.message);
    }
  }
);

// Update objective employee comment - PUT /:id/objectives/:objectiveId/employee-comments
export const updateObjectiveEmployeeComment = createAsyncThunk(
  "performanceReview/updateObjectiveEmployeeComment",
  async ({ reviewId, objectiveId, employeeComments }, { rejectWithValue }) => {
    try {
      const response = await hrmsUpdateRequest(
        `${API_URL}/${reviewId}/objectives/${objectiveId}/employee-comments`,
        { employeeComments }
      );
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || error.message);
    }
  }
);

// Update objective score (Admin, Final Year only) - PUT /:id/objectives/:objectiveId/score
export const updateObjectiveScore = createAsyncThunk(
  "performanceReview/updateObjectiveScore",
  async ({ reviewId, objectiveId, adminScore, adminComments }, { rejectWithValue }) => {
    try {
      const response = await hrmsUpdateRequest(
        `${API_URL}/${reviewId}/objectives/${objectiveId}/score`,
        { adminScore, adminComments: adminComments || "" }
      );
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || error.message);
    }
  }
);

// Complete review - PUT /:id/complete
export const completeReview = createAsyncThunk(
  "performanceReview/complete",
  async (reviewId, { rejectWithValue }) => {
    try {
      const response = await hrmsUpdateRequest(`${API_URL}/${reviewId}/complete`, {});
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || error.message);
    }
  }
);

const performanceReviewSlice = createSlice({
  name: "performanceReview",
  initialState: {
    reviews: [],
    loading: false,
    error: null,
  },
  reducers: {
    clearError: (state) => {
      state.error = null;
    },
    resetReviews: (state) => {
      state.reviews = [];
      state.loading = false;
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    // Create performance review
    builder
      .addCase(createPerformanceReview.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(createPerformanceReview.fulfilled, (state, action) => {
        state.loading = false;
        state.reviews.push(action.payload);
      })
      .addCase(createPerformanceReview.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      });

    // Fetch performance reviews by teacher
    builder
      .addCase(fetchPerformanceReviewsByTeacher.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchPerformanceReviewsByTeacher.fulfilled, (state, action) => {
        state.loading = false;
        state.reviews = action.payload;
      })
      .addCase(fetchPerformanceReviewsByTeacher.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      });

    // Fetch performance review by ID
    builder
      .addCase(fetchPerformanceReviewById.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchPerformanceReviewById.fulfilled, (state, action) => {
        state.loading = false;
        const index = state.reviews.findIndex((r) => r.id === action.payload.id);
        if (index !== -1) {
          state.reviews[index] = action.payload;
        } else {
          state.reviews.push(action.payload);
        }
      })
      .addCase(fetchPerformanceReviewById.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      });

    // Update admin comments
    builder
      .addCase(updateAdminComments.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(updateAdminComments.fulfilled, (state, action) => {
        state.loading = false;
        const index = state.reviews.findIndex((r) => r.id === action.payload.id);
        if (index !== -1) {
          state.reviews[index] = action.payload;
        }
      })
      .addCase(updateAdminComments.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      });

    // Update employee comments
    builder
      .addCase(updateEmployeeComments.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(updateEmployeeComments.fulfilled, (state, action) => {
        state.loading = false;
        const index = state.reviews.findIndex((r) => r.id === action.payload.id);
        if (index !== -1) {
          state.reviews[index] = action.payload;
        }
      })
      .addCase(updateEmployeeComments.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      });

    // Update objective employee comment
    builder
      .addCase(updateObjectiveEmployeeComment.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(updateObjectiveEmployeeComment.fulfilled, (state, action) => {
        state.loading = false;
        const index = state.reviews.findIndex((r) => r.id === action.payload.id);
        if (index !== -1) {
          state.reviews[index] = action.payload;
        }
      })
      .addCase(updateObjectiveEmployeeComment.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      });

    // Update objective score
    builder
      .addCase(updateObjectiveScore.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(updateObjectiveScore.fulfilled, (state, action) => {
        state.loading = false;
        const index = state.reviews.findIndex((r) => r.id === action.payload.id);
        if (index !== -1) {
          state.reviews[index] = action.payload;
        }
      })
      .addCase(updateObjectiveScore.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      });

    // Complete review
    builder
      .addCase(completeReview.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(completeReview.fulfilled, (state, action) => {
        state.loading = false;
        const index = state.reviews.findIndex((r) => r.id === action.payload.id);
        if (index !== -1) {
          state.reviews[index] = action.payload;
        }
      })
      .addCase(completeReview.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      });
  },
});

export const { clearError, resetReviews } = performanceReviewSlice.actions;
export default performanceReviewSlice.reducer;

